import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type TextboxSize = 'small' | 'medium' | 'large';
export type TextboxType = 'text' | 'email' | 'password' | 'number';

@Component({
  selector: 'app-textbox',
  templateUrl: './textbox.component.html',
  styleUrls: ['./textbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextboxComponent),
      multi: true
    }
  ]
})
export class TextboxComponent implements ControlValueAccessor, OnChanges {
  @ViewChild('input') inputElement!: ElementRef<HTMLInputElement>;

  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() value: string = '';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() error: boolean = false;
  @Input() size: TextboxSize = 'medium';
  @Input() type: TextboxType = 'text';
  @Input() prefixIcon: string = '';
  @Input() prefixText: string = '';
  @Input() suffixIcon: string = '';
  @Input() errorMessage: string = '';
  @Input() showClearButton: boolean = false;

  textboxId: string = `textbox-${Math.random().toString(36).substr(2, 9)}`;

  @Output() valueChange = new EventEmitter<string>();
  @Output() change = new EventEmitter<string>();
  @Output() focus = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && this.inputElement) {
      this.inputElement.nativeElement.value = this.value;
    }
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.change.emit(this.value);
  }

  onFocus(): void {
    this.focus.emit();
  }

  onBlur(): void {
    this.onTouched();
    this.blur.emit();
  }

  clearInput(): void {
    this.value = '';
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.change.emit(this.value);
    if (this.inputElement) {
      this.inputElement.nativeElement.focus();
    }
  }

  writeValue(value: string): void {
    this.value = value || '';
    if (this.inputElement) {
      this.inputElement.nativeElement.value = this.value;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getTextboxClasses(): string {
    const classes = ['textbox'];
    if (this.size) classes.push(`size-${this.size}`);
    if (this.disabled) classes.push('disabled');
    if (this.error) classes.push('error');
    if (this.required) classes.push('required');
    if (this.prefixIcon) classes.push('has-prefix-icon');
    if (this.prefixText) classes.push('has-prefix-text');
    if (this.suffixIcon || this.showClearButton) classes.push('has-suffix-icon');
    return classes.join(' ');
  }
}
